import { Link } from "react-router-dom";
import { translate } from "services/general";
import { hasAsideLinkPermission } from "services/user";
import { CRUMB_KIND } from "./constants";

// The ellipsis lists several sections in one menu, and two of them can hold the same
// page, so each section's keys are scoped by that section's crumb key.
export const scopedKey = (scope, key) => (scope ? `${scope}:${key}` : key);
export const isInScope = (key, scope) => key.startsWith(`${scope}:`);

// The section closest to the current page (the last one holding a selected key) opens
// with the sheet, so that page is in view; the sections above it stay one tap away
// rather than stacking every level open.
export function initialOpenKeys(items, selectedKeys = []) {
  const holding = items.filter(
    (item) =>
      item.children &&
      item.type !== "group" &&
      selectedKeys.some((key) => isInScope(key, item.key))
  );
  return holding.length ? [holding[holding.length - 1].key] : [];
}

const entryLabel = (entry) => translate(entry.text, entry.en_text);

const linkItem = (entry, scope) => ({
  key: scopedKey(scope, entry.path),
  label: <Link to={entry.path}>{entryLabel(entry)}</Link>,
});

/**
 * The pages a sidebar group holds, hidden by the same permission rule the sidebar
 * uses. A nested group becomes a labelled group of its pages.
 */
export function sectionItems(entry, scope) {
  return entry.children.flatMap((child) => {
    if (!child.children) {
      return hasAsideLinkPermission(child) ? [linkItem(child, scope)] : [];
    }
    const pages = child.children.filter(hasAsideLinkPermission);
    if (!pages.length) return [];
    return [
      {
        type: "group",
        key: scopedKey(scope, `group-${child.en_text}`),
        label: entryLabel(child),
        children: pages.map((page) => linkItem(page, scope)),
      },
    ];
  });
}

/** The levels a collapsed trail hides: sections as submenus, pages as links. */
export function hiddenItems(crumbs) {
  return crumbs.map((crumb) => {
    if (crumb.kind === CRUMB_KIND.SECTION) {
      const children = sectionItems(crumb.entry, crumb.key);
      return children.length
        ? { key: crumb.key, label: crumb.label, children }
        : { key: crumb.key, label: crumb.label, disabled: true };
    }
    return crumb.url
      ? { key: crumb.key, label: <Link to={crumb.url}>{crumb.label}</Link> }
      : { key: crumb.key, label: crumb.label, disabled: true };
  });
}

/** The current page's key inside each hidden section that lists it. */
export function hiddenSelectedKeys(crumbs) {
  return crumbs
    .filter((crumb) => crumb.activePath)
    .map((crumb) => scopedKey(crumb.key, crumb.activePath));
}
